/**
 * Module Registry — Single Source of Truth
 *
 * This file defines ALL module codes, their grouping, labels, icons, and routes.
 * The sidebar, permission checkboxes, and access-control logic all read from here.
 */

// ─── Interfaces ────────────────────────────────────────────────────────────

export interface ModuleEntry {
  key: ModuleCode;
  labelKey: string;       // i18n key
  path?: string;          // route path (if navigable)
  shortcut?: string;      // keyboard shortcut displayed in sidebar
  adminOnly?: boolean;    // only visible to admins
}

export interface ModuleGroup {
  key: string;
  labelKey: string;       // i18n key for group name
  icon?: string;          // Lucide icon name (for reference)
  children: (ModuleEntry | ModuleSubGroup)[];
  /** Module code(s) that gate visibility of this entire group */
  requiredModules?: ModuleCode[];
  /**
   * Phase 3 metadata — permission panel configuration.
   * Static sidebar sub-labels shown in the permission tree for this group
   * (e.g. Stock Management shows Warehouses, Warehouse Card, Stock Overview).
   */
  permissionStatic?: Array<{ labelKey: string; fallback: string; module: ModuleCode }>;
  /**
   * Phase 3 metadata — when true, this group is rendered as a flat (non-nested)
   * permission checkbox rather than a collapsible tree node.
   */
  permissionFlat?: boolean;
  /**
   * Phase 3 metadata — reserved for future use; currently unused in PERMISSION_TREE
   * but included for type completeness.
   */
  permissionMergedAs?: string;
}

export interface ModuleSubGroup {
  key: string;
  labelKey: string;
  children: ModuleEntry[];
}

/** Type guard */
export function isSubGroup(child: ModuleEntry | ModuleSubGroup): child is ModuleSubGroup {
  return 'children' in child;
}

// ─── Module Codes ──────────────────────────────────────────────────────────

export const MODULE_CODES = {
  // Master Data - Partners
  PARTNERS: 'PARTNERS',
  PARTNERS_SEARCH_BOARD: 'PARTNERS_SEARCH_BOARD',
  BANKS: 'BANKS',
  PARITIES: 'PARITIES',

  // Master Data - Items
  ITEMS: 'ITEMS',
  ACCOUNTING_TYPE: 'ACCOUNTING_TYPE',
  CLASSIFICATION: 'CLASSIFICATION',
  UNIT_MEASUREMENT: 'UNIT_MEASUREMENT',
  SERIAL_NUMBERS: 'SERIAL_NUMBERS',
  PACKAGING_MATERIALS: 'PACKAGING_MATERIALS',

  // Master Data - Production / Formulations
  INGREDIENTS: 'INGREDIENTS',
  INGREDIENTS_HISTORY: 'INGREDIENTS_HISTORY',
  COPY_INGREDIENTS: 'COPY_INGREDIENTS',
  USE_OF_COMPONENTS: 'USE_OF_COMPONENTS',
  PHASE_TYPES: 'PHASE_TYPES',
  // Referenced in SidebarDevelopment hasAccess() but not yet in permissions DB — tracked as anomaly
  MACHINES: 'MACHINES',
  PROCESS_DEFINITIONS: 'PROCESS_DEFINITIONS',
  SUBPROCESSES: 'SUBPROCESSES',
  PRODUCTION_STATUSES: 'PRODUCTION_STATUSES',
  PRODUCTION_EVENT_LOG: 'PRODUCTION_EVENT_LOG',

  // Master Data - Configuration
  CURRENCIES: 'CURRENCIES',
  VAT_TYPES: 'VAT_TYPES',
  COUNTRIES: 'COUNTRIES',
  MUNICIPALITIES: 'MUNICIPALITIES',

  // Documents
  DOCUMENTS_CONFIG: 'DOCUMENTS_CONFIG',
  PRINT_FORMS: 'PRINT_FORMS',
  COMMUNICATION_STATUSES: 'COMMUNICATION_STATUSES',
  PRINT_CONTROL_PANEL: 'PRINT_CONTROL_PANEL',
  MATERIAL_DOCUMENTS: 'MATERIAL_DOCUMENTS',
  NON_MATERIAL_DOCUMENTS: 'NON_MATERIAL_DOCUMENTS',
  WORK_ORDERS: 'WORK_ORDERS',

  // Administration Console
  SECURITY: 'SECURITY',
  COMPANY_SETTINGS: 'COMPANY_SETTINGS',
  DATABASE: 'DATABASE',
  // Grantable to non-admins (e.g. QA sector) so an admin can choose who sees the
  // navbar barcode-expiry warning without granting the admin-only COMPANY_SETTINGS
  // module. No screen/route of its own — lives in the qualityManagement registry
  // group below purely so it surfaces in the permission tree. Do NOT add a `path`.
  BARCODE_ALERTS: 'BARCODE_ALERTS',

  // Planning
  PLANNING_SALES_BUYERS: 'PLANNING_SALES_BUYERS',
  PLANNING_INFLOWS: 'PLANNING_INFLOWS',
  PLANNING_SALES: 'PLANNING_SALES',
  PLANNING_PRODUCTION: 'PLANNING_PRODUCTION',
  PLANNING_PROCUREMENT_VM: 'PLANNING_PROCUREMENT_VM',
  PLANNING_PROCUREMENT_SUPPLIERS: 'PLANNING_PROCUREMENT_SUPPLIERS',
  PLANNING_OUTFLOW_PROCUREMENT: 'PLANNING_OUTFLOW_PROCUREMENT',

  // Stock Management
  STOCK_MANAGEMENT: 'STOCK_MANAGEMENT',
  STOREHOUSE_SEARCH_BOARD: 'STOREHOUSE_SEARCH_BOARD',

  // Inventory
  INVENTORY: 'INVENTORY',

  // HR
  HR_BASIC_DATA: 'HR_BASIC_DATA',
  HR_DEPARTMENTS: 'HR_DEPARTMENTS',
  HR_M1M2: 'HR_M1M2',
  HR_CONTRACTS: 'HR_CONTRACTS',
  HR_CERTIFICATES: 'HR_CERTIFICATES',
  HR_K15: 'HR_K15',
  HR_WORKTIME: 'HR_WORKTIME',
  HR_STATISTICS: 'HR_STATISTICS',
  HR_TRAINING: 'HR_TRAINING',

  // Financial
  PAYMENT_METHODS: 'PAYMENT_METHODS',
  CASH_REGISTER: 'CASH_REGISTER',
  // Referenced in SidebarFinancial hasAccess() but not yet in permissions DB — tracked as anomaly
  FINANCE: 'FINANCE',

  // System (Admin Only)
  USERS: 'USERS',
  SECTORS: 'SECTORS',

  // Devices (merged: printers, phones, computers - single sidebar entry)
  DEVICES: 'DEVICES',
  // Legacy (kept for backward compat; permission UI shows only DEVICES)
  PRINTERS: 'PRINTERS',
  PHONES: 'PHONES',
  COMPUTERS: 'COMPUTERS',

  // Team Plan (Communication hub — no sidebar route, lives in /dashboard)
  PLAN_TEAM: 'PLAN_TEAM',

  // Quality Management
  QUALITY_GROUP_TYPE: 'QUALITY_GROUP_TYPE',
  QUALITY_DOCUMENTATION: 'QUALITY_DOCUMENTATION',
  QUALITY_CONTROL: 'QUALITY_CONTROL',

  // Development & Regulatory
  DEVELOPMENT: 'DEVELOPMENT',
  REGULATORY: 'REGULATORY',
} as const;

export type ModuleCode = typeof MODULE_CODES[keyof typeof MODULE_CODES];

// ─── MODULE_REGISTRY ───────────────────────────────────────────────────────
// Mirrors the exact sidebar structure: same groups, same order, same nesting.
// Dynamic sections (Planning, Procurement, Stock, Manufacturing, Sales, Invoices)
// render document types from DB — they appear here as a group with requiredModules
// but no static children.
//
// Render order in Sidebar.tsx (top to bottom):
//   Communication (hardcoded link — not a module)
//   1. SidebarMasterData    → definitions
//   2. SidebarPlanning      → planning
//   3. SidebarMaterial      → procurement
//   4. SidebarStockManagement → stockManagement
//   5. SidebarManufacturing → manufacturing
//   6. SidebarNonMaterial   → sales
//   7. SidebarDocumentation → qualityManagement
//   8. SidebarDevelopment   → development
//   9. SidebarRegulatory    → regulatory
//  10. Quality Control link (inline in Sidebar.tsx, gated on QUALITY_CONTROL)
//  11. SidebarInvoice       → invoices  (dynamic, gated on MATERIAL_DOCUMENTS|NON_MATERIAL_DOCUMENTS)
//  12. SidebarHR            → hr
//  13. SidebarFinancial     → financial
//  14. SidebarComputerSystem → devices
//  Footer: AI Chat in SidebarFooter help-dropdown (not a sidebar nav entry)

export const MODULE_REGISTRY: ModuleGroup[] = [
  // 1. Definitions (SidebarMasterData)
  {
    key: 'definitions',
    labelKey: 'sidebar.definitions',
    icon: 'Database',
    children: [
      {
        // Partners sub-dropdown: Partners, Banks, Parities, Currencies, Countries, Municipalities
        // + PRINT_CONTROL_PANEL → /partners-control-panel (gated separately)
        key: 'partners',
        labelKey: 'sidebar.partner',
        children: [
          { key: 'PARTNERS', labelKey: 'sidebar.partner', path: '/partners', shortcut: 'F11' },
          { key: 'PARTNERS_SEARCH_BOARD', labelKey: 'sidebar.partnersSearchBoard', path: '/partners-search-board' },
          { key: 'BANKS', labelKey: 'sidebar.banks', path: '/banks' },
          { key: 'PAYMENT_METHODS', labelKey: 'sidebar.paymentMethod', path: '/payment-methods' },
          { key: 'PARITIES', labelKey: 'sidebar.parities', path: '/parities' },
          { key: 'CURRENCIES', labelKey: 'tableHeaders.currency', path: '/currencies' },
          { key: 'COUNTRIES', labelKey: 'sidebar.countries', path: '/countries' },
          { key: 'MUNICIPALITIES', labelKey: 'sidebar.municipalities', path: '/municipalities' },
        ],
      },
      {
        // Items sub-dropdown: Items, AccountingType, UnitMeasurement, SerialNumbers,
        // PackagingMaterials, VatTypes
        // + PRINT_CONTROL_PANEL → /items-control-panel (gated separately)
        key: 'items',
        labelKey: 'tableHeaders.idents',
        children: [
          { key: 'ITEMS', labelKey: 'sidebar.items', path: '/items', shortcut: 'F12' },
          { key: 'ACCOUNTING_TYPE', labelKey: 'headers.accountingType', path: '/accounting-type' },
          { key: 'UNIT_MEASUREMENT', labelKey: 'tableHeaders.unitMeasurment', path: '/unit-measurment' },
          { key: 'SERIAL_NUMBERS', labelKey: 'tableHeaders.serialNumbers', path: '/serial-numbers' },
          { key: 'PACKAGING_MATERIALS', labelKey: 'sidebar.packagingMaterials', path: '/packing-materials' },
          { key: 'VAT_TYPES', labelKey: 'sidebar.vatTypes', path: '/vat' },
        ],
      },
      {
        // Classification sub-dropdown: 7 classification sub-type routes, all gated on CLASSIFICATION
        // + PRINT_CONTROL_PANEL → /classification-control-panel (gated separately)
        // NOTE: The sidebar renders 7 sub-routes under /classification/* but they all
        // share the single CLASSIFICATION module code. Path here is the base path only.
        key: 'classification',
        labelKey: 'tableHeaders.classification',
        children: [
          { key: 'CLASSIFICATION', labelKey: 'tableHeaders.classification', path: '/classification' },
        ],
      },
      {
        // Program sub-dropdown: Documents config, Administration Console (Security, Users,
        // Company Settings, Database import/export, Audit Trail admin-only), Print Forms
        key: 'program',
        labelKey: 'sidebar.program',
        children: [
          { key: 'DOCUMENTS_CONFIG', labelKey: 'sidebar.documents', path: '/documents' },
          { key: 'COMMUNICATION_STATUSES', labelKey: 'sidebar.communicationStatuses', path: '/communication-statuses' },
          { key: 'SECURITY', labelKey: 'sidebar.security', path: '/administration-console/security', adminOnly: true },
          { key: 'USERS', labelKey: 'sidebar.users', path: '/administration-console/users', adminOnly: true },
          { key: 'COMPANY_SETTINGS', labelKey: 'settings.company', path: '/company-settings', adminOnly: true },
          { key: 'DATABASE', labelKey: 'sidebar.databaseImportExport', path: '/administration-console/database/import-export', adminOnly: true },
          { key: 'PRINT_FORMS', labelKey: 'sidebar.printForms', path: '/print-forms' },
        ],
      },
    ],
  },

  // 2. Planning (SidebarPlanning) — dynamic doc types from DB (category === 'planning')
  {
    key: 'planning',
    labelKey: 'sidebar.planning',
    icon: 'BarChart',
    requiredModules: ['PLANNING_SALES_BUYERS', 'PLANNING_INFLOWS', 'PLANNING_SALES', 'PLANNING_PRODUCTION', 'PLANNING_PROCUREMENT_VM', 'PLANNING_PROCUREMENT_SUPPLIERS', 'PLANNING_OUTFLOW_PROCUREMENT'],
    children: [
      { key: 'PLANNING_SALES_BUYERS', labelKey: 'modules.PLANNING_SALES_BUYERS' },
      { key: 'PLANNING_INFLOWS', labelKey: 'modules.PLANNING_INFLOWS' },
      { key: 'PLANNING_SALES', labelKey: 'modules.PLANNING_SALES' },
      { key: 'PLANNING_PRODUCTION', labelKey: 'modules.PLANNING_PRODUCTION' },
      { key: 'PLANNING_PROCUREMENT_VM', labelKey: 'modules.PLANNING_PROCUREMENT_VM' },
      { key: 'PLANNING_PROCUREMENT_SUPPLIERS', labelKey: 'modules.PLANNING_PROCUREMENT_SUPPLIERS' },
      { key: 'PLANNING_OUTFLOW_PROCUREMENT', labelKey: 'modules.PLANNING_OUTFLOW_PROCUREMENT' },
    ],
  },

  // 3. Procurement (SidebarMaterial) — dynamic doc types
  // Renders: optional PRINT_CONTROL_PANEL → /procurement-control-panel, then DB docs
  // Gating: hasAccess('MATERIAL_DOCUMENTS')
  {
    key: 'procurement',
    labelKey: 'sidebar.procurement',
    icon: 'PackageCheck',
    requiredModules: ['MATERIAL_DOCUMENTS'],
    permissionStatic: [],
    children: [
      { key: 'MATERIAL_DOCUMENTS', labelKey: 'modules.MATERIAL_DOCUMENTS' },
    ],
  },

  // 4. Stock Management (SidebarStockManagement) — static links + dynamic warehouse docs
  // Static children: Warehouses (/stock-management), Warehouse Card, Stock Overview
  // + optional PRINT_CONTROL_PANEL → /stock-control-panel
  // + dynamic warehouse docs (menuGroup === 'warehouse')
  {
    key: 'stockManagement',
    labelKey: 'sidebar.stockManagement',
    icon: 'Warehouse',
    requiredModules: ['STOCK_MANAGEMENT'],
    permissionStatic: [
      { labelKey: 'sidebar.warehouses', fallback: 'Warehouses', module: 'STOCK_MANAGEMENT' },
      { labelKey: 'sidebar.documentMovements', fallback: 'Warehouse Card', module: 'STOCK_MANAGEMENT' },
      { labelKey: 'sidebar.stockOverview', fallback: 'Stock Overview', module: 'STOCK_MANAGEMENT' },
    ],
    children: [
      { key: 'STOCK_MANAGEMENT', labelKey: 'sidebar.stockManagement', path: '/stock-management' },
      { key: 'STOREHOUSE_SEARCH_BOARD', labelKey: 'sidebar.storehouseSearchBoard', path: '/storehouse/search-board' },
    ],
  },

  // 5. Manufacturing (SidebarManufacturing) — dynamic doc types
  // Renders WORK_ORDERS, DB docs + optional PRINT_CONTROL_PANEL → /production-control-panel.
  // Gating: hasAccess('WORK_ORDERS') || hasAccess('MATERIAL_DOCUMENTS') || manufacturingDocs.length > 0
  {
    key: 'manufacturing',
    labelKey: 'sidebar.manufacturing',
    icon: 'Factory',
    requiredModules: ['WORK_ORDERS'],
    permissionStatic: [],
    children: [
      { key: 'WORK_ORDERS', labelKey: 'modules.WORK_ORDERS' },
      { key: 'PRODUCTION_EVENT_LOG', labelKey: 'modules.PRODUCTION_EVENT_LOG', path: '/production-event-log' },
    ],
  },

  // 6. Sales (SidebarNonMaterial) — dynamic doc types
  // Renders: optional PRINT_CONTROL_PANEL → /sales-control-panel, then DB docs
  // Gating: hasAccess('NON_MATERIAL_DOCUMENTS')
  {
    key: 'sales',
    labelKey: 'sidebar.sales',
    icon: 'ShoppingCart',
    requiredModules: ['NON_MATERIAL_DOCUMENTS'],
    permissionStatic: [],
    children: [
      { key: 'NON_MATERIAL_DOCUMENTS', labelKey: 'modules.NON_MATERIAL_DOCUMENTS' },
    ],
  },

  // 7. Quality Management (SidebarDocumentation)
  // Renders: QUALITY_GROUP_TYPE → /qa/typeofgroup, QUALITY_DOCUMENTATION → /qa/qa,
  //          QUALITY_DOCUMENTATION → /qa-control-panel (control panel link)
  // NOTE: /qa-control-panel is gated on QUALITY_DOCUMENTATION in the sidebar but is
  // not a separate entry in the registry — it is treated as part of QUALITY_DOCUMENTATION.
  {
    key: 'qualityManagement',
    labelKey: 'sidebar.qualityManagement',
    icon: 'ShieldCheck',
    children: [
      { key: 'QUALITY_GROUP_TYPE', labelKey: 'sidebar.documentations', path: '/qa/typeofgroup' },
      { key: 'QUALITY_DOCUMENTATION', labelKey: 'sidebar.doc', path: '/qa/qa' },
      // No path: this is a permission-only entry (navbar barcode-expiry warning),
      // not a screen. Deliberately grouped under Quality Management so admins can
      // grant it to the QA sector; intentionally NOT added to ROUTE_TO_MODULE.
      { key: 'BARCODE_ALERTS', labelKey: 'modules.BARCODE_ALERTS' },
    ],
  },

  // 8. Development (SidebarDevelopment)
  // Top-level link: DEVELOPMENT → /development
  // Formulations sub-group: INGREDIENTS, COPY_INGREDIENTS, USE_OF_COMPONENTS,
  //   MACHINES (anomaly — not in DB permissions yet), PROCESS_DEFINITIONS (same),
  //   SUBPROCESSES (same), PHASE_TYPES
  // Note: INGREDIENTS_HISTORY is in MODULE_CODES and permissionTree but the sidebar
  //   does NOT render it as a standalone link (only checked in hasFormulationsAccess).
  //   It is kept in the registry for permission-panel completeness but has no path.
  {
    key: 'development',
    labelKey: 'sidebar.development',
    icon: 'FlaskConical',
    children: [
      { key: 'DEVELOPMENT', labelKey: 'sidebar.development', path: '/development' },
      {
        key: 'formulations',
        labelKey: 'sidebar.formulations',
        children: [
          { key: 'INGREDIENTS', labelKey: 'sidebar.osBasicSpecification', path: '/os' },
          { key: 'INGREDIENTS', labelKey: 'sidebar.normativArchive', path: '/os/archive' },
          { key: 'INGREDIENTS_HISTORY', labelKey: 'modules.INGREDIENTS_HISTORY' },
          { key: 'COPY_INGREDIENTS', labelKey: 'sidebar.copyIngredients', path: '/copy-ingredients' },
          { key: 'USE_OF_COMPONENTS', labelKey: 'sidebar.useOfComponents', path: '/use-of-components' },
          { key: 'MACHINES', labelKey: 'sidebar.machines', path: '/machines' },
          { key: 'PROCESS_DEFINITIONS', labelKey: 'sidebar.processDefinitions', path: '/process-definitions' },
          { key: 'SUBPROCESSES', labelKey: 'sidebar.subprocesses', path: '/subprocesses' },
          { key: 'PRODUCTION_STATUSES', labelKey: 'sidebar.productionStatuses', path: '/production-statuses' },
          { key: 'PHASE_TYPES', labelKey: 'sidebar.phaseTypes', path: '/phase-types' },
        ],
      },
    ],
  },

  // 9. Regulatory (SidebarRegulatory)
  // Rendered as a FLAT top-level link (no collapsible children), not a dropdown group.
  // The sidebar renders a plain <Link> icon + label directly, not a <button> toggle.
  {
    key: 'regulatory',
    labelKey: 'sidebar.regulatory',
    icon: 'Scale',
    permissionFlat: true,
    children: [
      { key: 'REGULATORY', labelKey: 'sidebar.regulatory', path: '/regulatory' },
    ],
  },

  // 10. Quality Control (inline link in Sidebar.tsx, not a SidebarXxx subcomponent)
  // Rendered as a flat top-level <Link> (ClipboardCheck icon), gated on QUALITY_CONTROL.
  // Sits between SidebarRegulatory and SidebarInvoice in the render order.
  {
    key: 'qualityControl',
    labelKey: 'sidebar.qualityControl',
    icon: 'ClipboardCheck',
    permissionFlat: true,
    children: [
      { key: 'QUALITY_CONTROL', labelKey: 'sidebar.qualityControl', path: '/quality-control' },
    ],
  },

  // 11. Invoices (SidebarInvoice) — dynamic doc types (subCategory === 'invoice')
  // Gating: hasAccess('MATERIAL_DOCUMENTS') || hasAccess('NON_MATERIAL_DOCUMENTS')
  // Conditionally hidden when no invoice docs exist in DB.
  {
    key: 'invoices',
    labelKey: 'sidebar.invoices',
    icon: 'FileText',
    requiredModules: ['MATERIAL_DOCUMENTS', 'NON_MATERIAL_DOCUMENTS'],
    children: [
      { key: 'MATERIAL_DOCUMENTS', labelKey: 'modules.MATERIAL_DOCUMENTS' },
      { key: 'NON_MATERIAL_DOCUMENTS', labelKey: 'modules.NON_MATERIAL_DOCUMENTS' },
    ],
  },

  // Inventory (SidebarInventory)
  // 3 document types: Попис (count) /material/inventory-count/:documentType,
  //   Вишок (surplus) /material/inventory-surplus/:documentType,
  //   Кусок (shortage) /material/inventory-shortage/:documentType
  // All three gate under a single INVENTORY module code — permissionStatic shows what's included.
  {
    key: 'inventory',
    labelKey: 'sidebar.inventory',
    icon: 'Boxes',
    requiredModules: ['INVENTORY'],
    permissionStatic: [
      { labelKey: 'sidebar.inventoryCount', fallback: 'Попис', module: 'INVENTORY' },
      { labelKey: 'sidebar.inventorySurplus', fallback: 'Вишок', module: 'INVENTORY' },
      { labelKey: 'sidebar.inventoryShortage', fallback: 'Кусок', module: 'INVENTORY' },
    ],
    children: [
      { key: 'INVENTORY', labelKey: 'sidebar.inventory', path: '/material/inventory-count' },
    ],
  },

  // 12. HR (SidebarHR)
  // Renders many sub-links all gated via HR_BASIC_DATA, HR_DEPARTMENTS, or isAdmin:
  //   /hr/basic-data, /hr/departments, /hr/workplaces,
  //   /hr/contract-types, /hr/education-levels, /hr/salary-grades,
  //   /hr/contributions, /hr/payroll
  // Registry children reflect only the distinct module codes that gate access.
  // The many sub-routes under HR_BASIC_DATA and HR_DEPARTMENTS are path variants of
  // the same modules — they are not separate module codes.
  {
    key: 'hr',
    labelKey: 'sidebar.hr',
    icon: 'Users',
    children: [
      { key: 'HR_BASIC_DATA', labelKey: 'sidebar.basicData', path: '/hr/basic-data' },
      { key: 'HR_DEPARTMENTS', labelKey: 'sidebar.departments', path: '/hr/departments' },
      { key: 'HR_M1M2', labelKey: 'sidebar.m1m2', path: '/hr/m1m2' },
      { key: 'HR_CONTRACTS', labelKey: 'sidebar.contracts', path: '/hr/contracts' },
      { key: 'HR_CERTIFICATES', labelKey: 'sidebar.certificates', path: '/hr/certificates' },
      { key: 'HR_K15', labelKey: 'sidebar.k15', path: '/hr/k15' },
      { key: 'HR_WORKTIME', labelKey: 'sidebar.workTime', path: '/hr/worktime' },
      { key: 'HR_STATISTICS', labelKey: 'sidebar.statistics', path: '/hr/statistics' },
      { key: 'HR_TRAINING', labelKey: 'sidebar.training', path: '/hr/training' },
      { key: 'HR_TRAINING', labelKey: 'sidebar.trainingTypes', path: '/hr/training-types' },
    ],
  },

  // 13. Financial Management (SidebarFinancial)
  // The sidebar renders a rich nested structure:
  //   General Ledger: chart-of-accounts, journal-entries, trial-balance, analytical-cards
  //   VAT: input-register, output-register, ddv-04
  //   Payments: payment-orders, cash-register
  //   Bank: statements
  //   Receivables & Payables: customers, suppliers, ios, aging
  //   Financial Reports: trial-balance, balance-sheet, income-statement
  //   UJP Filings: ddv-04
  // Per-entry gating: CASH_REGISTER for cash register, FINANCE for all other
  // financial screens. Accounting type remains an Ident codebook.
  {
    key: 'financial',
    labelKey: 'sidebar.financialManagement',
    icon: 'Banknote',
    children: [
      { key: 'FINANCE', labelKey: 'sidebar.financialManagement', path: '/finance' },
      { key: 'PAYMENT_METHODS', labelKey: 'modules.PAYMENT_METHODS' },
      { key: 'CASH_REGISTER', labelKey: 'modules.CASH_REGISTER', path: '/finance/payments/cash-register' },
    ],
  },

  // 14. Computer System / Devices (SidebarComputerSystem)
  // Renders: /computer-system/devices, /computer-system/computer-stock,
  //          /computer-system/credentials, /computer-system/it-procedures
  // All four links appear for any user with access to DEVICES|PRINTERS|PHONES|COMPUTERS.
  // The last three paths were missing from the registry; now tracked as children of DEVICES.
  {
    key: 'devices',
    labelKey: 'sidebar.computerSystem',
    icon: 'Monitor',
    children: [
      { key: 'DEVICES', labelKey: 'sidebar.devices', path: '/computer-system/devices' },
      { key: 'PRINTERS', labelKey: 'modules.PRINTERS' },
      { key: 'PHONES', labelKey: 'modules.PHONES' },
      { key: 'COMPUTERS', labelKey: 'modules.COMPUTERS' },
    ],
  },

  // 17. Admin-only (Sectors — not rendered in sidebar, only appears in permission panel)
  {
    key: 'admin',
    labelKey: 'moduleCategories.Admin',
    children: [
      { key: 'SECTORS', labelKey: 'modules.SECTORS', adminOnly: true },
    ],
  },
];

// ─── Helpers derived from registry ─────────────────────────────────────────

/**
 * Returns the canonical MODULE_REGISTRY array.
 * Future consumers should import this helper rather than the raw constant so
 * filtering or cloning logic can be added here without breaking callers.
 */
export function getModuleRegistryTree(): ModuleGroup[] {
  return MODULE_REGISTRY;
}

/** Flat list of all ModuleEntry items from the registry */
function flattenEntries(groups: ModuleGroup[]): ModuleEntry[] {
  const entries: ModuleEntry[] = [];
  for (const group of groups) {
    for (const child of group.children) {
      if (isSubGroup(child)) {
        entries.push(...child.children);
      } else {
        entries.push(child);
      }
    }
  }
  return entries;
}

export const ALL_REGISTRY_ENTRIES: ModuleEntry[] = flattenEntries(MODULE_REGISTRY);

/** All unique module codes from the registry (replaces MODULE_CATEGORIES.flat()) */
export const ALL_REGISTRY_MODULE_CODES: ModuleCode[] = Array.from(new Set(ALL_REGISTRY_ENTRIES.map(e => e.key)));

/**
 * Build MODULE_CATEGORIES-compatible map from registry.
 * Keys = group labelKey, Values = module codes in that group.
 * Used by legacy consumers during migration.
 */
export function registryToCategories(): Record<string, ModuleCode[]> {
  const result: Record<string, ModuleCode[]> = {};
  for (const group of MODULE_REGISTRY) {
    const codes: ModuleCode[] = [];
    for (const child of group.children) {
      if (isSubGroup(child)) {
        codes.push(...child.children.map(e => e.key));
      } else {
        codes.push(child.key);
      }
    }
    result[group.key] = Array.from(new Set(codes));
  }
  return result;
}

// ─── PrintForms lowercase key ↔ ModuleCode mapping ────────────────────────
// Maps the 29 lowercase camelCase keys (stored in the MongoDB `printforms`
// collection `modules` field) to their canonical ModuleCode.
// The remaining 15 keys (Group b) have no clean registry equivalent and
// are handled with hardcoded fallbacks in PrintForms.tsx.
// ⚠️  Do NOT rename these string values — they are stored in MongoDB.

export const MODULE_CODE_TO_LOWER_KEY: Partial<Record<ModuleCode, string>> = {
  // Master Data — Partners
  PARTNERS: 'partners',
  BANKS: 'banks',
  CURRENCIES: 'currencies',
  COUNTRIES: 'countries',
  MUNICIPALITIES: 'municipalities',
  PARITIES: 'parities',

  // Master Data — Items
  ITEMS: 'items',
  ACCOUNTING_TYPE: 'accountingTypes',
  CLASSIFICATION: 'classifications',
  UNIT_MEASUREMENT: 'unitMeasurements',
  SERIAL_NUMBERS: 'serialNumbers',
  PACKAGING_MATERIALS: 'packagingMaterials',
  VAT_TYPES: 'vatTypes',

  // Development / Formulations
  INGREDIENTS: 'ingredients',
  USE_OF_COMPONENTS: 'useOfComponents',

  // Program / Config
  PRINT_FORMS: 'printForms',
  COMMUNICATION_STATUSES: 'communicationStatuses',
  DOCUMENTS_CONFIG: 'documents',

  // Documents
  MATERIAL_DOCUMENTS: 'materialDocuments',
  NON_MATERIAL_DOCUMENTS: 'nonMaterialDocuments',
  WORK_ORDERS: 'workOrders',

  // Quality Management
  QUALITY_DOCUMENTATION: 'qualityDocumentation',
  QUALITY_CONTROL: 'qualityControl',

  // Development & Regulatory
  DEVELOPMENT: 'development',
  REGULATORY: 'regulatory',

  // HR
  HR_BASIC_DATA: 'hrBasicData',
  HR_DEPARTMENTS: 'hrDepartments',

  // Financial / Devices
  PAYMENT_METHODS: 'paymentMethods',
  DEVICES: 'devices',
};

/** Returns the lowercase camelCase key for a ModuleCode, or null if not mapped. */
export function camelKeyFromModuleCode(code: ModuleCode): string | null {
  return MODULE_CODE_TO_LOWER_KEY[code] ?? null;
}

/** Returns the ModuleCode for a lowercase camelCase key, or null if not mapped. */
export function moduleCodeFromCamelKey(key: string): ModuleCode | null {
  for (const [code, lower] of Object.entries(MODULE_CODE_TO_LOWER_KEY)) {
    if (lower === key) return code as ModuleCode;
  }
  return null;
}

// ─── Legacy constants (kept for backward compat) ──────────────────────────

// Modules that only admins can access (must match registry entries with adminOnly: true)
export const ADMIN_ONLY_MODULES: ModuleCode[] = ['SECTORS', 'USERS', 'SECURITY', 'COMPANY_SETTINGS', 'DATABASE'];

// All planning sub-modules
export const PLANNING_MODULES: ModuleCode[] = [
  'PLANNING_SALES_BUYERS',
  'PLANNING_INFLOWS',
  'PLANNING_SALES',
  'PLANNING_PRODUCTION',
  'PLANNING_PROCUREMENT_VM',
  'PLANNING_PROCUREMENT_SUPPLIERS',
  'PLANNING_OUTFLOW_PROCUREMENT',
];

// Module display categories for UI grouping (same as sidebar)
export const MODULE_CATEGORIES: Record<string, ModuleCode[]> = {
  'Partners': ['PARTNERS', 'PARTNERS_SEARCH_BOARD', 'BANKS', 'PARITIES', 'CURRENCIES', 'COUNTRIES', 'MUNICIPALITIES'],
  'Item': ['ITEMS', 'ACCOUNTING_TYPE', 'CLASSIFICATION', 'UNIT_MEASUREMENT', 'SERIAL_NUMBERS', 'PACKAGING_MATERIALS', 'VAT_TYPES'],
  'Production': ['INGREDIENTS', 'INGREDIENTS_HISTORY', 'COPY_INGREDIENTS', 'USE_OF_COMPONENTS', 'PHASE_TYPES'],
  'Program': ['DOCUMENTS_CONFIG', 'COMMUNICATION_STATUSES', 'MATERIAL_DOCUMENTS', 'NON_MATERIAL_DOCUMENTS'],
  'Administration Console': ['SECURITY', 'USERS', 'COMPANY_SETTINGS', 'DATABASE', 'PRINT_FORMS', 'PRINT_CONTROL_PANEL'],
  'Admin': ['USERS', 'SECTORS'],
  'Planning': ['PLANNING_SALES_BUYERS', 'PLANNING_INFLOWS', 'PLANNING_SALES', 'PLANNING_PRODUCTION', 'PLANNING_PROCUREMENT_VM', 'PLANNING_PROCUREMENT_SUPPLIERS', 'PLANNING_OUTFLOW_PROCUREMENT'],
  'Stock Management': ['STOCK_MANAGEMENT', 'STOREHOUSE_SEARCH_BOARD'],
  'Manufacturing': ['WORK_ORDERS', 'PRODUCTION_EVENT_LOG'],
  'HR': ['HR_BASIC_DATA', 'HR_DEPARTMENTS', 'HR_M1M2', 'HR_CONTRACTS', 'HR_CERTIFICATES', 'HR_K15', 'HR_WORKTIME', 'HR_STATISTICS', 'HR_TRAINING'],
  'Financial': ['FINANCE', 'PAYMENT_METHODS', 'CASH_REGISTER'],
  'Devices': ['DEVICES', 'PRINTERS', 'PHONES', 'COMPUTERS'],
  'Quality Management': ['QUALITY_GROUP_TYPE', 'QUALITY_DOCUMENTATION', 'QUALITY_CONTROL'],
  'Development': ['DEVELOPMENT'],
  'Regulatory': ['REGULATORY'],
};

/** Same order and grouping as sidebar: Definitions first (Partners, Item, Production, Program, Admin), then Planning, Stock, HR, Financial, Devices, AI */
export type PermissionSidebarEntry =
  | { group: string; children: string[] }
  | { group: string; sidebarSubLabels: string[]; children: string[] }  // sub-labels shown like sidebar (e.g. Warehouses, Warehouse Card, Stock Overview)
  | string;
export const PERMISSION_SIDEBAR_ORDER: PermissionSidebarEntry[] = [
  { group: 'Definitions', children: ['Partners', 'Item', 'Production', 'Program', 'Admin'] },
  'Planning',
  { group: 'Stock Management', sidebarSubLabels: ['sidebar.warehouses', 'sidebar.documentMovements', 'sidebar.stockOverview', 'sidebar.transferDocuments'], children: ['Stock Management'] },
  { group: 'Manufacturing', children: ['Manufacturing'] },
  'HR',
  'Financial',
  'Devices',
];

// Route to module mapping
// NOTE: getModuleForPath uses longest-prefix matching, so entry order does not matter.
export const ROUTE_TO_MODULE: Record<string, ModuleCode> = {
  // Master Data - Partners
  '/partners': 'PARTNERS',
  '/partners-search-board': 'PARTNERS_SEARCH_BOARD',
  '/banks': 'BANKS',
  '/parities': 'PARITIES',
  '/banks-control-panel': 'BANKS',
  '/parities-control-panel': 'PARITIES',
  '/currencies-control-panel': 'CURRENCIES',
  '/countries-control-panel': 'COUNTRIES',
  '/municipalities-control-panel': 'MUNICIPALITIES',

  // Master Data - Items
  '/items': 'ITEMS',
  '/accounting-type': 'ACCOUNTING_TYPE',
  '/classification': 'CLASSIFICATION',
  // P3 fix: route is '/unit-measurment' (no trailing s) — was '/unit-measurments'
  '/unit-measurment': 'UNIT_MEASUREMENT',
  '/serial-numbers': 'SERIAL_NUMBERS',
  '/packing-materials': 'PACKAGING_MATERIALS',
  '/items-control-panel': 'ITEMS',
  '/items-print-preview': 'ITEMS',
  '/partners-control-panel': 'PARTNERS',
  '/partners-print-preview': 'PARTNERS',
  '/contracted-prices-control-panel': 'PARTNERS',
  '/contracted-prices-print-preview': 'PARTNERS',
  '/contracted-prices-print-preview/:formId': 'PARTNERS',
  '/classification-control-panel': 'CLASSIFICATION',
  '/accounting-types-control-panel': 'ACCOUNTING_TYPE',
  '/unit-measurements-control-panel': 'UNIT_MEASUREMENT',
  '/serial-numbers-control-panel': 'SERIAL_NUMBERS',
  '/packaging-materials-control-panel': 'PACKAGING_MATERIALS',
  '/vat-types-control-panel': 'VAT_TYPES',

  // Master Data - Production / Formulations
  '/os': 'INGREDIENTS',
  '/os/archive': 'INGREDIENTS',
  '/copy-ingredients': 'COPY_INGREDIENTS',
  '/use-of-components': 'USE_OF_COMPONENTS',
  '/phase-types': 'PHASE_TYPES',
  '/machines': 'MACHINES',
  '/process-definitions': 'PROCESS_DEFINITIONS',
  '/subprocesses': 'SUBPROCESSES',
  '/production-statuses': 'PRODUCTION_STATUSES',
  '/ingridiens-control-panel': 'INGREDIENTS',
  '/ingridiens-print-preview': 'INGREDIENTS',

  // Master Data - Configuration
  '/currencies': 'CURRENCIES',
  '/vat': 'VAT_TYPES',
  '/countries': 'COUNTRIES',
  '/municipalities': 'MUNICIPALITIES',

  // Documents / Config
  // P3 fix: route is '/documents' — also keep '/documents-config' for any legacy links
  '/documents': 'DOCUMENTS_CONFIG',
  '/documents-config': 'DOCUMENTS_CONFIG',
  '/communication-statuses': 'COMMUNICATION_STATUSES',
  '/qa/documents': 'DOCUMENTS_CONFIG',
  '/print-forms': 'PRINT_FORMS',
  '/print-form-designer': 'PRINT_FORMS',
  '/print-preview': 'PRINT_FORMS',
  '/generic-print-preview': 'PRINT_FORMS',

  // Administration Console (admin-only — also enforced by isAdminOnlyModule check)
  '/administration-console/security': 'SECURITY',
  '/company-settings': 'COMPANY_SETTINGS',
  '/administration-console/database': 'DATABASE',

  // Material Documents
  // P2: inventory routes are MORE specific than '/material' — longest-prefix wins
  '/material/inventory-count': 'INVENTORY',
  '/material/inventory-surplus': 'INVENTORY',
  '/material/inventory-shortage': 'INVENTORY',
  '/material': 'MATERIAL_DOCUMENTS',
  '/procurement': 'MATERIAL_DOCUMENTS',
  '/procurement-control-panel': 'MATERIAL_DOCUMENTS',
  '/procurement-print-preview': 'MATERIAL_DOCUMENTS',

  // Non-Material Documents
  '/non-material': 'NON_MATERIAL_DOCUMENTS',
  '/sales-control-panel': 'NON_MATERIAL_DOCUMENTS',
  '/sales-print-preview': 'NON_MATERIAL_DOCUMENTS',

  // Invoices and Packing Lists (P3 fix: was missing)
  // /invoice (ФАК/billing) is gated as NON_MATERIAL on the backend (invoice.js) — keep in sync.
  '/invoice': 'NON_MATERIAL_DOCUMENTS',
  '/packing-list': 'MATERIAL_DOCUMENTS',

  // Work Orders + optional PRINT_CONTROL_PANEL production print routes
  '/work-orders': 'WORK_ORDERS',
  '/production-control-panel': 'WORK_ORDERS',
  '/production-print-preview': 'WORK_ORDERS',
  '/work-order': 'WORK_ORDERS',
  '/production-event-log': 'PRODUCTION_EVENT_LOG',

  // Inventory (P2)
  '/inventory-count-print-preview': 'INVENTORY',
  '/inventory-adjustment-print-preview': 'INVENTORY',

  // Stock Management
  '/stock-management': 'STOCK_MANAGEMENT',
  '/stock-control-panel': 'STOCK_MANAGEMENT',
  '/warehouse-card-control-panel': 'STOCK_MANAGEMENT',
  '/warehouse-card-print-preview': 'STOCK_MANAGEMENT',
  '/storehouse/search-board': 'STOREHOUSE_SEARCH_BOARD',

  // HR
  '/hr/basic-data': 'HR_BASIC_DATA',
  '/hr/departments': 'HR_DEPARTMENTS',
  '/hr/workplaces': 'HR_DEPARTMENTS',
  '/hr/contract-types': 'HR_BASIC_DATA',
  '/hr/contributions': 'HR_BASIC_DATA',
  '/hr/payroll': 'HR_BASIC_DATA',
  '/hr/holidays': 'HR_BASIC_DATA',
  '/hr/absence-types': 'HR_BASIC_DATA',
  '/hr/shifts': 'HR_BASIC_DATA',
  '/hr/m1m2': 'HR_M1M2',
  '/hr/contracts': 'HR_CONTRACTS',
  '/hr/certificates': 'HR_CERTIFICATES',
  '/hr/k15': 'HR_K15',
  '/hr/worktime': 'HR_WORKTIME',
  '/hr/statistics': 'HR_STATISTICS',
  '/hr/training': 'HR_TRAINING',
  '/hr/training-types': 'HR_TRAINING',
  '/hr/education-levels': 'HR_BASIC_DATA',
  '/hr/salary-grades': 'HR_BASIC_DATA',

  // Financial
  '/payment-methods': 'PAYMENT_METHODS',
  '/finance/payments/cash-register': 'CASH_REGISTER',
  '/finance/general-ledger/chart-of-accounts': 'FINANCE',
  '/finance': 'FINANCE',

  // System (Admin Only — also enforced by isAdminOnlyModule check)
  '/users': 'USERS',
  '/administration-console/users': 'USERS',

  // Devices (merged printers, phones, computers)
  '/computer-system/devices': 'DEVICES',
  '/computer-system/computer-stock': 'DEVICES',
  '/computer-system/credentials': 'DEVICES',
  '/computer-system/it-procedures': 'DEVICES',
  '/computer-system-control-panel': 'DEVICES',
  '/computer-system-print-preview': 'DEVICES',

  // Quality Management
  // P3 fix: was missing
  '/qa/typeofgroup': 'QUALITY_GROUP_TYPE',
  '/qa/qa': 'QUALITY_DOCUMENTATION',
  '/qa-control-panel': 'QUALITY_DOCUMENTATION',
  '/qual-doc-control-panel': 'QUALITY_DOCUMENTATION',
  '/qual-doc-print-preview': 'QUALITY_DOCUMENTATION',
  '/certificate-preview': 'QUALITY_DOCUMENTATION',

  // Development & Regulatory (P3 fix: was missing)
  '/development': 'DEVELOPMENT',
  '/regulatory': 'REGULATORY',
  '/quality-control': 'QUALITY_CONTROL',
};

/**
 * Get the module code for a given path.
 * Handles dynamic routes like /material/issuance/internal/1700.
 * Returns null for planning routes (handled separately via hasAnyPlanningAccess).
 *
 * Matching strategy:
 *   1. Exact match — fastest path.
 *   2. Longest-prefix match — most specific registered prefix wins, so
 *      '/material/inventory-count/9A00' resolves to INVENTORY (via
 *      '/material/inventory-count') rather than MATERIAL_DOCUMENTS (via
 *      '/material').  Entry order in ROUTE_TO_MODULE does NOT matter.
 */
export function getModuleForPath(path: string): ModuleCode | null {
  // Planning routes are handled separately — return null so ProtectedRoute
  // can check for any planning module access
  if (path === '/planning' || path.startsWith('/planning/')) {
    return null;
  }

  // 1. Direct match
  if (ROUTE_TO_MODULE[path]) {
    return ROUTE_TO_MODULE[path];
  }

  // 2. Longest-prefix match (most specific wins)
  let bestMatch: ModuleCode | null = null;
  let bestLength = 0;
  for (const [route, module] of Object.entries(ROUTE_TO_MODULE)) {
    if (path.startsWith(route + '/') && route.length > bestLength) {
      bestMatch = module;
      bestLength = route.length;
    }
  }
  return bestMatch;
}

/** Document type codes that are manufacturing/work-order; access via /material/... also requires WORK_ORDERS doc permission */

/**
 * Check if the path is a planning route
 */
export function isPlanningRoute(path: string): boolean {
  return path === '/planning' || path.startsWith('/planning/');
}

/**
 * Check if a module code is admin-only
 */
export function isAdminOnlyModule(moduleCode: ModuleCode): boolean {
  return ADMIN_ONLY_MODULES.includes(moduleCode);
}
